<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch, type PropType } from 'vue'
import { SolidFill, ColorHEX, emptyLine, FontSettings, AxisTickStrategies, Themes } from '@lightningchart/lcjs'
import UpgradePremium from '~/pages/upgrade-premium.vue';
const { hasPremiumAccess } = useAuth();

interface PumpOverviewItem {
  holeNo: number
  maxPressure: number
  cement1: number
  cement2: number
  additive1?: number
  additive2?: number
  additive3?: number
  accelerator?: number
}

interface StatusItem {
  holeNo: number
  eventType: string
}

const props = defineProps({
  materialOverviewConsumedFromPumps: {
    type: Array as PropType<PumpOverviewItem[]>,
    required: true,
    default: () => []
  },
  statusData: {
    type: Array as PropType<StatusItem[]>,
    default: () => []
  },
  setupData: {
    type: Array as PropType<any[]>,
    default: () => []
  }
})

const PRESSURE_LOW = 10

// Derive end pressure from the most recent setup session — mode across all pumps.
// Skid SetupData is SetupItem[][] — an array of sessions, each session being
// an array of per-pump items sharing the same timestamp.
const stopPressureInfo = computed(() => {
  const sessions: any[][] = props.setupData || []
  if (!sessions.length) return { value: 25, inconsistent: false, allValues: [] as number[] }

  // Sort sessions by the timestamp of their first item, newest first
  const latestSession = [...sessions].sort((a, b) => {
    const tA = a.length ? new Date(a[0].timestamp).getTime() : 0
    const tB = b.length ? new Date(b[0].timestamp).getTime() : 0
    return tB - tA
  })[0]

  const values: number[] = (latestSession || [])
    .map((c: any) => c.endPressure)
    .filter((p: number) => p > 0)

  if (!values.length) return { value: 25, inconsistent: false, allValues: [] }

  const freq = new Map<number, number>()
  values.forEach(p => freq.set(p, (freq.get(p) || 0) + 1))
  const maxFreq = Math.max(...freq.values())
  const topValues = [...freq.entries()].filter(([, f]) => f === maxFreq).map(([v]) => v)
  let threshold: number
  if (topValues.length === 1) {
    threshold = topValues[0]
  } else {
    // Tie — fall back to median of all values
    const sorted = [...values].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    threshold = sorted.length % 2 === 0
      ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
      : sorted[mid]
  }

  return {
    value: threshold,
    inconsistent: new Set(values).size > 1,
    allValues: [...new Set(values)].sort((a, b) => a - b)
  }
})

const PRESSURE_HIGH = computed(() => stopPressureInfo.value.value)

// endAmount is in litres (not kg) so used for info only — no chart reference line
const maxVolumeInfo = computed(() => {
  const sessions: any[][] = props.setupData || []
  if (!sessions.length) return { value: 0, unit: 'L' }
  const latestSession = [...sessions].sort((a, b) => {
    const tA = a.length ? new Date(a[0].timestamp).getTime() : 0
    const tB = b.length ? new Date(b[0].timestamp).getTime() : 0
    return tB - tA
  })[0]
  const values: number[] = (latestSession || [])
    .map((c: any) => c.endAmount)
    .filter((v: number) => v > 0)
  if (!values.length) return { value: 0, unit: 'L' }
  const freq = new Map<number, number>()
  values.forEach(v => freq.set(v, (freq.get(v) || 0) + 1))
  const maxFreq = Math.max(...freq.values())
  const topValues = [...freq.entries()].filter(([, f]) => f === maxFreq).map(([v]) => v)
  let threshold: number
  if (topValues.length === 1) {
    threshold = topValues[0]
  } else {
    const sorted = [...values].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    threshold = sorted.length % 2 === 0
      ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
      : sorted[mid]
  }
  return { value: threshold, unit: 'L' }
})

const COLOR_LOW  = '#e53935'
const COLOR_MED  = '#f0b323'
const COLOR_HIGH = '#4caf50'

const chartContainer = ref<HTMLDivElement>()
let chart: any = undefined
let seriesRefs: any[] = []

const fmt = (val: number | undefined, dec = 1) => {
  if (val === undefined || val === null) return '0'
  return val.toLocaleString(undefined, { maximumFractionDigits: dec })
}

const scatterData = computed(() => {
  return (props.materialOverviewConsumedFromPumps || [])
    .map(item => ({
      holeNo: item.holeNo,
      cement: (item.cement1 || 0) + (item.cement2 || 0),
      pressure: item.maxPressure || 0
    }))
    .filter(d => d.cement > 0 || d.pressure > 0)
    .sort((a, b) => a.holeNo - b.holeNo)
})

const getZone = (pressure: number): 'low' | 'moderate' | 'high' => {
  if (pressure < PRESSURE_LOW) return 'low'
  if (pressure < PRESSURE_HIGH.value) return 'moderate'
  return 'high'
}

const zoneConfig = computed(() => ({
  low:      { label: `Low (< ${PRESSURE_LOW} bar)`,                               color: 'error',   hex: COLOR_LOW },
  moderate: { label: `Moderate (${PRESSURE_LOW}–${PRESSURE_HIGH.value} bar)`,     color: 'warning', hex: COLOR_MED },
  high:     { label: `High (≥ ${PRESSURE_HIGH.value} bar)`,                       color: 'success', hex: COLOR_HIGH }
}))

const stats = computed(() => {
  const data = scatterData.value
  if (!data.length) return null
  const pressures = data.map(d => d.pressure)
  const cements   = data.map(d => d.cement)
  const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0)
  return {
    holeCount:         data.length,
    maxPressure:       Math.max(...pressures),
    avgPressure:       sum(pressures) / pressures.length,
    totalCement:       sum(cements),
    highPressureCount: data.filter(d => d.pressure >= PRESSURE_HIGH.value).length,
    lowPressureCount:  data.filter(d => d.pressure < PRESSURE_LOW).length
  }
})

const initChart = () => {
  if (!chartContainer.value) return
  const lc = useUseLightningCharts()

  const TEXT_DARK = ColorHEX('#1e2d3d')
  const TEXT_MID  = ColorHEX('#4b5e6d')
  const PAGE_BG   = ColorHEX('#f5f5f5')
  const axisFont  = new FontSettings().setSize(11).setFamily('Arial').setWeight('normal')
  const titleFont = new FontSettings().setSize(14).setFamily('Arial').setWeight('bold')

  chart = lc.ChartXY({ container: chartContainer.value, theme: Themes.light })
    .setTitle('Max Pressure vs Total Cement — Ground Condition Analysis')
    .setTitleFont(titleFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_DARK }))
    .setTitleEffect(false)
    .setBackgroundFillStyle(new SolidFill({ color: PAGE_BG }))
    .setBackgroundStrokeStyle(emptyLine)
    .setSeriesBackgroundFillStyle(new SolidFill({ color: ColorHEX('#ffffff') }))
    .setSeriesBackgroundEffect(false)
    .setAnimationsEnabled(false)

  chart.getDefaultAxisX()
    .setTitle('Total Cement Injected (kg)')
    .setTitleFont(axisFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_MID }))
    .setTickStrategy(AxisTickStrategies.Numeric, (ticks: any) =>
      ticks.setMajorTickStyle((major: any) =>
        major.setLabelFont(axisFont).setLabelFillStyle(new SolidFill({ color: TEXT_MID }))
      )
    )

  chart.getDefaultAxisY()
    .setTitle('Max Pressure (bar)')
    .setTitleFont(axisFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_MID }))
    .setTickStrategy(AxisTickStrategies.Numeric, (ticks: any) =>
      ticks.setMajorTickStyle((major: any) =>
        major.setLabelFont(axisFont).setLabelFillStyle(new SolidFill({ color: TEXT_MID }))
      )
    )

  chart.setCursorFormatting((_chart: any, hit: any) => {
    if (hit.x === undefined || hit.y === undefined) return []
    const data = scatterData.value
    if (!data.length) return []
    const dataRangeX = Math.max(...data.map(d => d.cement)) - Math.min(...data.map(d => d.cement))
    const { start, end } = chart.getDefaultAxisX().getInterval()
    if (dataRangeX === 0 || (end - start) >= dataRangeX * 0.95) return []
    const d = data.find(item => item.cement === hit.x && item.pressure === hit.y)
    if (!d) return []
    return [
      [`Hole ${d.holeNo}`],
      ['Cement', '', `${d.cement.toFixed(1)} kg`],
      ['Max Pressure', '', `${d.pressure.toFixed(1)} bar`]
    ]
  })

  if (scatterData.value.length > 0) {
    applyChartData(scatterData.value)
  }
}

const applyChartData = (data: typeof scatterData.value) => {
  seriesRefs.forEach(s => s.dispose())
  seriesRefs = []

  const zones: Array<{ key: 'low' | 'moderate' | 'high'; label: string; hex: string }> = [
    { key: 'low',      label: zoneConfig.value.low.label,      hex: COLOR_LOW },
    { key: 'moderate', label: zoneConfig.value.moderate.label, hex: COLOR_MED },
    { key: 'high',     label: zoneConfig.value.high.label,     hex: COLOR_HIGH }
  ]

  zones.forEach(zone => {
    const zoneData = data.filter(d => getZone(d.pressure) === zone.key)
    if (zoneData.length === 0) return

    const series = chart.addPointSeries({
      name: zone.label,
      schema: { x: { pattern: null }, y: { pattern: null } }
    })
    series.setPointSize(10)
    series.setPointFillStyle(new SolidFill({ color: ColorHEX(zone.hex) }))
    series.appendJSON(zoneData.map(d => ({ x: d.cement, y: d.pressure })))
    seriesRefs.push(series)
  })
}

onMounted(() => { initChart() })

onUnmounted(() => {
  chart?.dispose()
  chart = undefined
  seriesRefs = []
})

watch(scatterData, (newData) => {
  if (!chart || !newData.length) return
  applyChartData(newData)
})

const stopReasonMap = computed(() => {
  const map = new Map<number, string>()
  for (const s of (props.statusData || [])) {
    map.set(s.holeNo, s.eventType || '')
  }
  return map
})

const getStopColor = (eventType: string) => {
  const s = (eventType || '').toLowerCase()
  if (s.includes('reached')) return 'success'
  if (s.includes('not finalized') || s.includes('working')) return 'warning'
  if (s.includes('error') || s.includes('stop')) return 'error'
  return 'default'
}

const tableHeaders = [
  { title: 'Hole No',            key: 'holeNo',     align: 'start'  as const },
  { title: 'Total Cement (kg)',  key: 'cement',     align: 'end'    as const },
  { title: 'Max Pressure (bar)', key: 'pressure',   align: 'end'    as const },
  { title: 'Zone',               key: 'zone',       align: 'center' as const },
  { title: 'Stop Reason',        key: 'stopReason', align: 'start'  as const },
]

const tableData = computed(() =>
  scatterData.value
    .map(d => ({
      ...d,
      zone: getZone(d.pressure),
      stopReason: stopReasonMap.value.get(d.holeNo) ?? ''
    }))
    .sort((a, b) => b.pressure - a.pressure)
)

const holeMap = computed(() =>
  scatterData.value
    .map(d => ({ ...d, zone: getZone(d.pressure) }))
    .sort((a, b) => a.holeNo - b.holeNo)
)

const holeMapTooltip = (d: { holeNo: number; pressure: number; cement: number; zone: string }) =>
  `Hole ${d.holeNo} · ${d.pressure.toFixed(1)} bar · ${d.cement.toFixed(1)} kg`

const materialByZone = computed(() => {
  const data = scatterData.value
  const totalCement = data.reduce((sum, d) => sum + d.cement, 0)
  return (['high', 'moderate', 'low'] as const).map(zone => {
    const holes = data.filter(d => getZone(d.pressure) === zone)
    const cement = holes.reduce((sum, d) => sum + d.cement, 0)
    return {
      zone,
      holes: holes.length,
      cement,
      avgCement: holes.length ? cement / holes.length : 0,
      pct: totalCement > 0 ? (cement / totalCement) * 100 : 0
    }
  })
})
</script>

<template>
  <UpgradePremium v-if="!hasPremiumAccess" style="min-height: 400px;" />
    <v-container fluid v-else>
      <v-alert
        v-if="!materialOverviewConsumedFromPumps || materialOverviewConsumedFromPumps.length === 0"
        type="info"
        variant="tonal"
        text="No pump data available for pressure/volume analysis."
        class="mb-4"
      />

      <template v-else>
        <!-- KPI strip -->
        <v-row class="mb-6" dense>
          <v-col cols="6" md="3">
            <v-card elevation="1" class="text-center pa-4">
              <div class="text-h4 font-weight-bold text-blue-grey-darken-3">{{ stats?.holeCount ?? 0 }}</div>
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mt-1">Holes Analysed</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card elevation="1" class="text-center pa-4">
              <div class="text-h4 font-weight-bold text-blue-grey-darken-3">{{ fmt(stats?.maxPressure) }}</div>
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mt-1">Peak Pressure (bar)</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card elevation="1" class="text-center pa-4">
              <div class="text-h4 font-weight-bold text-blue-grey-darken-3">{{ fmt(stats?.avgPressure) }}</div>
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mt-1">Avg Pressure (bar)</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card elevation="1" class="text-center pa-4">
              <div class="text-h4 font-weight-bold text-blue-grey-darken-3">{{ fmt(stats?.totalCement, 0) }}</div>
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mt-1">Total Cement (kg)</div>
            </v-card>
          </v-col>
        </v-row>
        
        <!-- Hole Completion Map -->
        <v-card elevation="2" class="mb-6">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4 py-3 px-4">
            Screen Overview
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <div class="hole-map">
              <v-tooltip v-for="hole in holeMap" :key="hole.holeNo" location="top" :text="holeMapTooltip(hole)">
                <template v-slot:activator="{ props }">
                  <div v-bind="props" class="hole-cell" :style="{ backgroundColor: zoneConfig[hole.zone].hex }">
                    <span class="hole-label">{{ hole.holeNo }}</span>
                  </div>
                </template>
              </v-tooltip>
            </div>
            <div class="d-flex align-center mt-3" style="gap: 32px;">
              <div v-for="(cfg, key) in zoneConfig" :key="key" class="d-flex align-center" style="gap: 6px;">
                <div class="zone-dot" :style="{ backgroundColor: cfg.hex }" />
                <span class="text-caption text-medium-emphasis">{{ cfg.label }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>



        <!-- Chart + Guide -->
        <v-row class="mb-6">
          <v-col cols="12" md="8">
            <v-card elevation="2">
              <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
                Pressure / Cement Scatter
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-0">
                <div ref="chartContainer" style="height: 420px; width: 100%;" />
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card elevation="2" class="h-100">
              <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
                Ground Condition Guide
              </v-card-title>
              <v-divider />
              <v-card-text>
                <v-alert
                  v-if="stopPressureInfo.inconsistent"
                  type="warning"
                  variant="tonal"
                  density="compact"
                  class="mb-3 text-caption"
                >
                  Pump end pressures vary: {{ stopPressureInfo.allValues.join(', ') }} bar. Using {{ PRESSURE_HIGH }} bar (most common).
                </v-alert>
                <div class="mb-4">
                  <div v-for="(cfg, key) in zoneConfig" :key="key" class="d-flex align-center mb-2">
                    <v-chip size="small" :color="cfg.color" variant="flat" class="mr-2 font-weight-bold" style="min-width: 36px; justify-content: center;">
                      {{ tableData.filter(d => d.zone === key).length }}
                    </v-chip>
                    <span class="text-body-2">{{ cfg.label }}</span>
                  </div>
                </div>

                <v-divider class="mb-4" />

                <div class="text-body-2 mb-3">
                  <div class="d-flex align-start mb-3">
                    <v-icon color="success" size="18" class="mr-2 mt-0.5">mdi-arrow-top-right</v-icon>
                    <div class="font-weight-bold">High pressure + high cement</div>
                  </div>
                  <div class="d-flex align-start mb-3">
                    <v-icon color="success" size="18" class="mr-2 mt-0.5">mdi-arrow-top-left</v-icon>
                    <div class="font-weight-bold">High pressure + low cement</div>
                  </div>
                  <div class="d-flex align-start mb-3">
                    <v-icon color="error" size="18" class="mr-2 mt-0.5">mdi-arrow-bottom-right</v-icon>
                    <div class="font-weight-bold">Low pressure + high cement</div>
                  </div>
                  <div class="d-flex align-start">
                    <v-icon color="error" size="18" class="mr-2 mt-0.5">mdi-arrow-bottom-left</v-icon>
                    <div class="font-weight-bold">Low pressure + low cement</div>
                  </div>
                </div>

                <v-divider class="my-3" />
                <div class="text-caption text-medium-emphasis">
                  Zoom in and hover over scatter points to inspect individual holes. Thresholds: low &lt; {{ PRESSURE_LOW }} bar, high ≥ {{ PRESSURE_HIGH }} bar.
                </div>

                <div v-if="maxVolumeInfo.value > 0" class="d-flex align-center mt-2" style="gap: 8px;">
                  <v-icon size="14" color="purple">mdi-gauge</v-icon>
                  <span class="text-caption text-medium-emphasis">
                    Max volume limit: <strong>{{ fmt(maxVolumeInfo.value, 0) }} L</strong> per pump
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>


        <!-- Material by Zone -->
        <v-card elevation="2" class="mb-6">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4 py-3 px-4">
            Material by Zone
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-row dense>
              <v-col v-for="row in materialByZone" :key="row.zone" cols="12" md="4">
                <v-card variant="outlined" class="pa-4">
                  <div class="d-flex align-center mb-3" style="gap: 10px;">
                    <v-chip size="small" :color="zoneConfig[row.zone].color" variant="flat" class="font-weight-bold text-uppercase">
                      {{ row.zone }}
                    </v-chip>
                    <span class="text-caption text-medium-emphasis">{{ zoneConfig[row.zone].label }}</span>
                  </div>
                  <v-progress-linear
                    :model-value="row.pct"
                    :color="zoneConfig[row.zone].color"
                    bg-color="grey-lighten-3"
                    rounded
                    height="8"
                    class="mb-3"
                  />
                  <v-row dense>
                    <v-col cols="4" class="text-center">
                      <div class="text-h6 font-weight-bold" :style="{ color: zoneConfig[row.zone].hex }">{{ row.holes }}</div>
                      <div class="text-caption text-medium-emphasis">Holes</div>
                    </v-col>
                    <v-col cols="4" class="text-center">
                      <div class="text-h6 font-weight-bold" :style="{ color: zoneConfig[row.zone].hex }">{{ fmt(row.cement, 0) }}</div>
                      <div class="text-caption text-medium-emphasis">Total kg</div>
                    </v-col>
                    <v-col cols="4" class="text-center">
                      <div class="text-h6 font-weight-bold" :style="{ color: zoneConfig[row.zone].hex }">{{ fmt(row.pct, 1) }}%</div>
                      <div class="text-caption text-medium-emphasis">of Total</div>
                    </v-col>
                  </v-row>
                  <div class="text-caption text-medium-emphasis mt-2 text-right">
                    Avg {{ fmt(row.avgCement, 1) }} kg / hole
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Detail table -->
        <v-card elevation="2">
          <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
            <span class="text-subtitle-1 font-weight-bold">Per-Hole Detail</span>
            <span class="text-caption text-medium-emphasis">Sorted by pressure — highest first</span>
          </v-card-title>
          <v-divider />
          <v-data-table :headers="tableHeaders" :items="tableData" density="compact" class="text-body-2" hover :items-per-page="25">
            <template v-slot:item.holeNo="{ item }">
              <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
                {{ item.holeNo }}
              </v-chip>
            </template>
            <template v-slot:item.cement="{ item }">
              <span class="font-monospace">{{ fmt(item.cement) }}</span>
            </template>
            <template v-slot:item.pressure="{ item }">
              <span class="font-monospace font-weight-medium">{{ fmt(item.pressure) }}</span>
            </template>
            <template v-slot:item.zone="{ item }">
              <v-chip size="small" :color="zoneConfig[item.zone].color" variant="tonal" label class="font-weight-bold text-uppercase">
                {{ item.zone }}
              </v-chip>
            </template>
            <template v-slot:item.stopReason="{ item }">
              <v-chip
                v-if="item.stopReason"
                size="small"
                :color="getStopColor(item.stopReason)"
                variant="tonal"
                label
                class="font-weight-medium text-uppercase"
              >
                {{ item.stopReason }}
              </v-chip>
              <span v-else class="text-medium-emphasis text-caption">—</span>
            </template>
          </v-data-table>
        </v-card>
      </template>
  </v-container>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}

.hole-map {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.hole-cell {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  transition: transform 0.1s, box-shadow 0.1s;
}

.hole-cell:hover {
  transform: scale(1.15);
  box-shadow: 0 2px 8px rgba(0,0,0,0.25);
  z-index: 1;
  position: relative;
}

.hole-label {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0,0,0,0.4);
}

.zone-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
